import './coapplicantEmploymentDetailsPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { phoneNumberWithCountryCode },
  } = useLeadChannelStore();
  const { next, previous } = useWorkflowStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeEmployerName,
    coapplicantCurrentIncomeGrossAnnual,
    coapplicantCurrentIncomeJobTitle,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeEmployerName,
    setCoapplicantCurrentIncomeGrossAnnual,
    setCoapplicantCurrentIncomeJobTitle,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      employerNameValue: coapplicantCurrentIncomeEmployerName,
      jobTitleValue: coapplicantCurrentIncomeJobTitle,
      grossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantCurrentIncomeGrossAnnual,
      ),
    },
    delayError: 200,
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { employerNameValue, jobTitleValue, grossAnnualIncomeValue } = values;

    if (!employerNameValue || !jobTitleValue || !grossAnnualIncomeValue) {
      return;
    }

    setCoapplicantCurrentIncomeEmployerName(employerNameValue);
    setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);
    setCoapplicantCurrentIncomeJobTitle(jobTitleValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-employment-details-page"
        sx={theme.pageLayout}
      >
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEmploymentDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantEmploymentDetails.form.h1}
              >
                {t('CoapplicantEmploymentDetailsPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantEmploymentDetails.form.h2}
              >
                {t('CoapplicantEmploymentDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEmploymentDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('employerNameValue', employerNameValidation)}
                    error={!isEmpty(errors.employerNameValue)}
                    label={t('CoapplicantEmploymentDetailsPage.employerName')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEmploymentDetails.form
                            .employerNameInput,
                      },
                    }}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.employerNameValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.coapplicantEmploymentDetails.form
                          .employerNameInputError
                      }
                    >
                      {toString(errors.employerNameValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('jobTitleValue', jobTitleValidation)}
                    error={!isEmpty(errors.jobTitleValue)}
                    label={t('CoapplicantEmploymentDetailsPage.jobTitle')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEmploymentDetails.form
                            .jobTitleInput,
                      },
                    }}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.jobTitleValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.coapplicantEmploymentDetails.form
                          .jobTitleInputError
                      }
                    >
                      {toString(errors.jobTitleValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'grossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    error={!isEmpty(errors.grossAnnualIncomeValue)}
                    label={t(
                      'CoapplicantEmploymentDetailsPage.grossAnnualIncome',
                    )}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEmploymentDetails.form
                            .grossAnnualIncomeInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'grossAnnualIncomeValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.grossAnnualIncomeValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.coapplicantEmploymentDetails.form
                          .grossAnnualIncomeInputError
                      }
                    >
                      {toString(errors.grossAnnualIncomeValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantEmploymentDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantEmploymentDetailsPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={locator.pages.coapplicantEmploymentDetails.form.incomeDisclosure}
              >
                <Trans
                  components={{
                    phone: <a href={`tel:${phoneNumberWithCountryCode}`} />,
                  }}
                  i18nKey="disclosures.coapplicantIncome"
                  values={{ phoneNumber: phoneNumberWithCountryCode }}
                />
              </p>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantEmploymentDetails.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEmploymentDetailsPage';
